import { useRef, useState } from "react";
import { Star, X, ImagePlus, Instagram, Facebook, Globe, Music2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/lib/i18n";
import { api, errMsg, imgUrl } from "@/lib/api";

const EMPTY = { author: "", email: "", location: "", rating: 5, text: "", media: [], social: { instagram: "", facebook: "", tiktok: "", website: "" } };
const SOC = [["instagram", Instagram, "@usuario"], ["facebook", Facebook, "facebook.com/…"], ["tiktok", Music2, "@usuario"], ["website", Globe, "https://…"]];

export const ReviewForm = ({ open, onOpenChange, slug, onDone }) => {
  const { t, lang } = useI18n();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [up, setUp] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");
  const fileRef = useRef(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const upload = async (files) => {
    setUp(true);
    setErr("");
    for (const file of Array.from(files || []).slice(0, 6 - f.media.length)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/uploads/review", fd);
        setF((x) => ({ ...x, media: [...x.media, data] }));
      } catch (e) { setErr(errMsg(e, t("rv.err"))); }
    }
    setUp(false);
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(`/properties/${slug}/reviews`, { ...f, lang });
      setDone(data?.status === "published" ? t("rv.thanksNow") : t("rv.thanks"));
      setF(EMPTY);
      onDone?.();
    } catch (ex) { setErr(errMsg(ex, t("rv.err"))); } finally { setBusy(false); }
  };
  const close = (v) => { onOpenChange(v); if (!v) setDone(""); };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="review-form-dialog" data-lenis-prevent className="max-h-[92dvh] overflow-y-auto rounded-3xl border-0 bg-paper p-6 text-ink sm:max-w-lg sm:p-8">
        <DialogTitle className="font-display text-3xl font-medium">{t("rv.write")}</DialogTitle>
        {done ? (
          <div className="py-6 text-center" data-testid="review-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-forest" strokeWidth={1.3} />
            <p className="mt-4 text-[15px]">{done}</p>
            <button onClick={() => close(false)} className="mt-6 rounded-xl border border-ink/20 px-6 py-2.5 text-sm font-semibold">{t("f.close")}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted2">{t("rv.rating")}</p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} data-testid={`review-star-${n}`} onClick={() => set("rating", n)}><Star className={`h-7 w-7 ${n <= f.rating ? "fill-accent2 text-accent2" : "text-ink/25"}`} /></button>
                ))}
              </div>
            </div>
            <Input data-testid="review-name-input" required minLength={2} placeholder={t("rv.name")} value={f.author} onChange={(e) => set("author", e.target.value)} className="h-11 rounded-xl bg-white" />
            <div className="grid gap-3 sm:grid-cols-2">
              <Input data-testid="review-email-input" type="email" placeholder={t("rv.email")} value={f.email} onChange={(e) => set("email", e.target.value)} className="h-11 rounded-xl bg-white" />
              <Input data-testid="review-location-input" placeholder={t("rv.from")} value={f.location} onChange={(e) => set("location", e.target.value)} className="h-11 rounded-xl bg-white" />
            </div>
            <Textarea data-testid="review-text-input" required minLength={10} placeholder={t("rv.text")} value={f.text} onChange={(e) => set("text", e.target.value)} className="min-h-[120px] rounded-xl bg-white" />
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted2">{t("rv.media")}</p>
              <div className="flex flex-wrap gap-2">
                {f.media.map((m, i) => (
                  <div key={i} className="relative h-20 w-20 overflow-hidden rounded-xl bg-[#E8D8C3]">
                    {m.type === "video" ? <video src={imgUrl(m.url)} className="h-full w-full object-cover" muted /> : <img src={imgUrl(m.url)} alt="" className="h-full w-full object-cover" />}
                    <button type="button" onClick={() => set("media", f.media.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"><X className="h-3 w-3" /></button>
                  </div>
                ))}
                {f.media.length < 6 && (
                  <button type="button" data-testid="review-add-media" onClick={() => fileRef.current?.click()} className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-ink/30 text-[11px]">
                    <ImagePlus className="h-5 w-5" />{up ? t("rv.uploading") : t("rv.add")}
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" hidden multiple accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime" onChange={(e) => upload(e.target.files)} data-testid="review-file-input" />
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted2">{t("rv.social")}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {SOC.map(([k, I, ph]) => (
                  <label key={k} className="flex items-center gap-2 rounded-xl border border-ink/15 bg-white px-3">
                    <I className="h-4 w-4 shrink-0 text-muted2" />
                    <input data-testid={`review-social-${k}`} placeholder={ph} value={f.social[k]} onChange={(e) => set("social", { ...f.social, [k]: e.target.value })} className="h-10 w-full bg-transparent text-sm outline-none" />
                  </label>
                ))}
              </div>
            </div>
            {err && <p data-testid="review-error" className="rounded-xl bg-cta/10 p-3 text-sm text-[#8f3c20]">{err}</p>}
            <button data-testid="review-submit-button" disabled={busy || up} className="w-full rounded-2xl bg-forest py-3.5 font-semibold text-white disabled:opacity-60">{busy ? t("rv.sending") : t("rv.send")}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
